'use client';

import Link from 'next/link';
import { Hourglass, Accessibility, MessageSquareHeart, Users, ArrowRight, ExternalLink } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useScrollReveal } from '@/app/hooks/use-scroll-reveal';

const VALUES = [
  { icon: Hourglass, title: 'Long-term thinking', desc: 'We build to last. No shortcuts, no tech debt disguised as velocity.' },
  { icon: Accessibility, title: 'Accessibility-first', desc: 'Every interface we ship meets WCAG 2.2 AA. Communities include everyone.' },
  { icon: MessageSquareHeart, title: 'Open & honest', desc: 'We share progress, problems, and trade-offs openly. No surprises.' },
  { icon: Users, title: 'Built on community', desc: 'We build products for communities, with communities, shaped by their feedback.' },
] as const;

const STATS = [
  { value: '26', label: 'Counties targeted' },
  { value: '1', label: 'Platform built' },
  { value: '100%', label: 'Irish-owned' },
  { value: '1', label: 'Mission' },
] as const;

export function AboutPage() {
  const prefersReducedMotion = useReducedMotion();
  const { ref: valuesRef, isVisible: valuesVisible } = useScrollReveal();
  const { ref: statsRef, isVisible: statsVisible } = useScrollReveal();

  return (
    <>
      {/* Page hero */}
      <section className="bg-sandron-ink-50 pb-16 pt-[clamp(96px,12vw,160px)]">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 lg:px-0">
          <div className="flex items-center gap-3">
            <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
              About
            </span>
          </div>
          <h1 className="mt-6 max-w-[800px] font-display text-[clamp(40px,5vw,72px)] font-extrabold leading-[1.04] tracking-[-0.025em] text-sandron-ink-900 [text-wrap:balance]">
            We build software for the long haul, alongside the communities we serve.
          </h1>
          <p className="mt-6 max-w-[640px] text-[clamp(17px,1.4vw,20px)] leading-[1.6] text-sandron-ink-700">
            Sandron Infotech is a Dublin-based studio that designs, builds, and
            operates digital products for community organisations across Ireland.
          </p>
        </div>
      </section>

      {/* Story block */}
      <section className="py-[clamp(80px,10vw,120px)]">
        <div className="mx-auto grid max-w-[1200px] items-start gap-12 px-5 md:px-10 lg:grid-cols-[1.1fr_1fr] lg:gap-20 lg:px-0">
          <div>
            <h2 className="font-display text-[clamp(28px,3.5vw,44px)] font-bold leading-[1.12] tracking-[-0.02em] text-sandron-ink-900">
              Our story
            </h2>
            <div className="mt-5 space-y-4 text-[17px] leading-[1.65] text-sandron-ink-700">
              <p>
                We started Sandron Infotech because we saw community
                organisations struggling with outdated, fragmented tools that
                were never built for them.
              </p>
              <p>
                Our first product, SpaceNest, grew out of conversations with
                community centre managers who were juggling spreadsheets, paper
                diaries, and disconnected payment systems.
              </p>
              <p>
                Today, SpaceNest serves centres across Ireland — and we&apos;re just
                getting started. We operate what we build, so we feel every
                friction point and fix it fast.
              </p>
            </div>
          </div>
          {/* Placeholder photo */}
          <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-sandron-ink-100 bg-gradient-to-br from-sandron-ink-50 via-white to-sandron-ink-100">
            <div className="absolute bottom-4 left-4 rounded-xl bg-white/90 px-3 py-2 text-xs text-sandron-ink-500 backdrop-blur-sm">
              Sandron team &middot; Dublin, 2026
            </div>
          </div>
        </div>
      </section>

      {/* Values grid */}
      <section className="bg-sandron-ink-50 py-[clamp(80px,10vw,120px)]">
        <div ref={valuesRef} className="mx-auto max-w-[1200px] px-5 md:px-10 lg:px-0">
          <div className="mb-12 max-w-[720px]">
            <div className="flex items-center gap-3">
              <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
                What we believe
              </span>
            </div>
            <h2 className="mt-4 font-display text-[clamp(32px,4vw,56px)] font-bold leading-[1.1] tracking-[-0.02em] text-sandron-ink-900">
              Four principles that shape our work.
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 24 }}
                animate={valuesVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
                transition={{
                  duration: prefersReducedMotion ? 0.15 : 0.6,
                  delay: i * 0.09,
                  ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
                }}
                className="flex flex-col rounded-[20px] border border-sandron-ink-100 bg-white p-6 lg:p-8"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-[14px] bg-sandron-ink-50 text-sandron-ink-900">
                  <v.icon size={28} strokeWidth={2} aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-display text-[22px] font-bold leading-[1.2] text-sandron-ink-900">
                  {v.title}
                </h3>
                <p className="mt-2.5 text-[15px] leading-[1.6] text-sandron-ink-700">
                  {v.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section ref={statsRef} className="py-[clamp(80px,10vw,120px)]">
        <div className="mx-auto grid max-w-[1200px] grid-cols-2 gap-8 px-5 md:px-10 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-sandron-ink-100 lg:px-0">
          {STATS.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 16 }}
              animate={statsVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
              transition={{
                duration: prefersReducedMotion ? 0.15 : 0.6,
                delay: i * 0.09,
                ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
              }}
              className="text-center lg:px-8"
            >
              <span className="font-display text-[clamp(48px,6vw,88px)] font-extrabold leading-none tracking-[-0.025em] text-sandron-ink-900">
                {stat.value}
              </span>
              <p className="mt-3 text-[13px] font-medium uppercase tracking-[0.08em] text-sandron-ink-500">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden bg-sandron-ink-900 py-[clamp(96px,12vw,160px)]">
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[80px] motion-safe:animate-glow-drift"
          style={{ background: 'radial-gradient(circle, rgba(31,107,68,0.3) 0%, transparent 70%)' }}
          aria-hidden="true"
        />
        <div className="relative z-10 mx-auto max-w-[700px] px-5 text-center">
          <h2 className="font-display text-[clamp(32px,4.5vw,56px)] font-bold leading-[1.1] tracking-[-0.02em] text-white">
            Want to talk to us?
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-sandron-ink-300">
            Whether it&apos;s a partnership, a product idea, or just a chat about
            community tech — we&apos;re always happy to connect.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              href="/contact"
              className="inline-flex h-14 items-center gap-2.5 rounded-full bg-white px-7 font-display text-base font-semibold text-sandron-ink-900 transition-all duration-200 hover:-translate-y-px hover:bg-sandron-ink-50"
            >
              Get in touch <ArrowRight size={16} />
            </Link>
            <Link
              href="/products/spacenest"
              className="inline-flex h-14 items-center gap-2.5 rounded-full border border-white/20 px-7 font-display text-base font-semibold text-white transition-all duration-200 hover:border-white/40 hover:bg-white/[0.06]"
            >
              Visit SpaceNest <ExternalLink size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
